import { ArrowDown, ArrowUp, ChevronLeft, ChevronRight, ChevronsUpDown } from 'lucide-react';
import {
  createContext,
  useContext,
  useEffect,
  useRef,
  useState,
  type ComponentProps,
  type ReactNode,
} from 'react';
import { Button } from '@/components/ui/button';
import { Skeleton } from '@/components/ui/skeleton';
import { cn } from '@/lib/utils';

/*
 * The shared data-table primitives. Every list screen used to hand-roll the
 * same `<table className="w-full min-w-[…] border-collapse text-sm">` plus a
 * dozen repeated cell classes, which is how the three tables drifted apart:
 * different paddings, a colSpan hard-coded to a column count that no longer
 * matched, numbers left-aligned in one place and right-aligned in another.
 *
 * Two structural decisions are load-bearing:
 *
 *  - `border-separate border-spacing-0` rather than `border-collapse`. A
 *    collapsed border belongs to the table, not the cell, so a sticky header
 *    row loses its bottom border the moment it detaches and scrolls. Separate
 *    borders stay on the cell and travel with it.
 *  - The column count lives in context. Message and skeleton rows need a
 *    colSpan, and every screen here has columns that appear or disappear with
 *    the caller's role or scope. Reading it from the table rather than from a
 *    literal at the call site is what stops the two from drifting.
 */

const ColumnCountContext = createContext(1);

export type CellAlign = 'left' | 'right';

/**
 * The horizontally scrollable viewport around a table.
 *
 * A wide table clipped at the card's edge looks finished: there is no
 * scrollbar until you hover, and nothing says the last columns exist at all.
 * The gradient at each overflowing edge is the affordance. `tabIndex` follows
 * the same fact — while the region actually scrolls it has to be reachable by
 * keyboard (WCAG 2.1.1), and while it does not, an extra tab stop on a plain
 * div is just noise.
 */
export function TableScroller({
  label,
  boundHeight = false,
  className,
  children,
}: {
  label: string;
  /** Cap the viewport so the header can stick and the pager stays on screen. */
  boundHeight?: boolean;
  className?: string;
  children: ReactNode;
}) {
  const viewportRef = useRef<HTMLDivElement>(null);
  const [overflow, setOverflow] = useState({ start: false, end: false });

  useEffect(() => {
    const viewport = viewportRef.current;
    if (!viewport) return;

    const update = () => {
      const max = viewport.scrollWidth - viewport.clientWidth;
      // 1px, not 0: fractional layout widths leave a sub-pixel of scroll
      // behind on a table that visibly fits, and the fade would never clear.
      setOverflow({ start: viewport.scrollLeft > 1, end: max - viewport.scrollLeft > 1 });
    };

    update();
    viewport.addEventListener('scroll', update, { passive: true });

    // Rows arriving, a column toggling, the window resizing: all change
    // scrollWidth without firing a scroll event. jsdom has no ResizeObserver,
    // and nothing asserts on the fades, so it stays optional.
    const observer = typeof ResizeObserver === 'undefined' ? null : new ResizeObserver(update);
    observer?.observe(viewport);
    if (viewport.firstElementChild) observer?.observe(viewport.firstElementChild);

    return () => {
      viewport.removeEventListener('scroll', update);
      observer?.disconnect();
    };
  }, []);

  const scrollable = overflow.start || overflow.end;

  return (
    <div className="relative">
      <div
        ref={viewportRef}
        role="region"
        aria-label={label}
        // eslint-disable-next-line jsx-a11y/no-noninteractive-tabindex -- a scrollable region must be keyboard reachable; the rule's allowlist stops at tabpanel.
        tabIndex={scrollable ? 0 : undefined}
        className={cn(
          'overflow-x-auto focus-visible:outline-offset-[-2px]',
          boundHeight && 'max-h-[70vh] overflow-y-auto',
          className,
        )}
      >
        {children}
      </div>
      <div
        aria-hidden
        className={cn(
          'from-background/90 pointer-events-none absolute inset-y-0 left-0 w-8 bg-gradient-to-r to-transparent transition-opacity duration-200',
          overflow.start ? 'opacity-100' : 'opacity-0',
        )}
      />
      <div
        aria-hidden
        className={cn(
          'from-background/90 pointer-events-none absolute inset-y-0 right-0 w-8 bg-gradient-to-l to-transparent transition-opacity duration-200',
          overflow.end ? 'opacity-100' : 'opacity-0',
        )}
      />
    </div>
  );
}

export function Table({
  columnCount,
  className,
  ...props
}: ComponentProps<'table'> & { columnCount: number }) {
  return (
    <ColumnCountContext.Provider value={columnCount}>
      <table
        className={cn('w-full border-separate border-spacing-0 text-sm', className)}
        {...props}
      />
    </ColumnCountContext.Provider>
  );
}

export function TableHeader({
  sticky = false,
  className,
  ...props
}: ComponentProps<'thead'> & { sticky?: boolean }) {
  return (
    <thead
      className={cn(
        'text-muted-foreground text-left text-xs tracking-wide uppercase',
        // On the cells rather than the <thead>: Safari still ignores sticky on
        // a row group. The translucent fill plus blur keeps the rows sliding
        // underneath legible as motion without letting them read as text.
        sticky &&
          '[&_th]:bg-background/95 [&_th]:sticky [&_th]:top-0 [&_th]:z-10 [&_th]:backdrop-blur',
        className,
      )}
      {...props}
    />
  );
}

export function TableHead({
  align = 'left',
  className,
  ...props
}: ComponentProps<'th'> & { align?: CellAlign }) {
  return (
    <th
      scope="col"
      className={cn(
        'border-border/60 border-b px-4 py-3 font-medium whitespace-nowrap',
        align === 'right' && 'text-right',
        className,
      )}
      {...props}
    />
  );
}

/**
 * A sortable column header.
 *
 * The button carries `uppercase` and `tracking-wide` itself because the
 * browser's form-control defaults reset `text-transform` and `letter-spacing`
 * inside a button, so these labels came out title-cased next to their
 * upper-cased neighbours. The neutral chevron on hover/focus is the other half
 * of the fix: before it, nothing distinguished a sortable column from a fixed
 * one until you had already clicked it.
 */
export function TableSortHead({
  label,
  active,
  direction,
  onSort,
  align = 'left',
  className,
}: {
  label: string;
  active: boolean;
  direction: 'asc' | 'desc';
  onSort: () => void;
  align?: CellAlign;
  className?: string;
}) {
  return (
    <TableHead
      align={align}
      aria-sort={active ? (direction === 'asc' ? 'ascending' : 'descending') : 'none'}
      className={cn('p-0', className)}
    >
      <button
        type="button"
        onClick={onSort}
        className={cn(
          'hover:text-foreground group flex w-full items-center gap-1.5 px-4 py-3 text-xs tracking-wide uppercase transition-colors',
          align === 'right' && 'justify-end',
        )}
      >
        {label}
        {active ? (
          direction === 'asc' ? (
            <ArrowUp className="text-foreground size-3.5 shrink-0" aria-hidden />
          ) : (
            <ArrowDown className="text-foreground size-3.5 shrink-0" aria-hidden />
          )
        ) : (
          <ChevronsUpDown
            className="size-3.5 shrink-0 opacity-0 transition-opacity group-hover:opacity-70 group-focus-visible:opacity-70"
            aria-hidden
          />
        )}
      </button>
    </TableHead>
  );
}

export function TableBody({ className, ...props }: ComponentProps<'tbody'>) {
  // The last row's border would otherwise double up with the card footer's.
  return <tbody className={cn('[&>tr:last-child>td]:border-b-0', className)} {...props} />;
}

export function TableRow({ className, ...props }: ComponentProps<'tr'>) {
  return (
    <tr
      className={cn(
        'hover:bg-secondary/25 focus-within:bg-secondary/25 transition-colors',
        className,
      )}
      {...props}
    />
  );
}

export function TableCell({
  align = 'left',
  numeric = false,
  muted = false,
  className,
  ...props
}: ComponentProps<'td'> & {
  align?: CellAlign;
  /** Right-aligns and uses tabular figures, so digits line up column-wise. */
  numeric?: boolean;
  muted?: boolean;
}) {
  return (
    <td
      className={cn(
        'border-border/60 border-b px-4 py-3 align-middle',
        (numeric || align === 'right') && 'text-right',
        numeric && 'tabular-nums',
        muted && 'text-muted-foreground',
        className,
      )}
      {...props}
    />
  );
}

/**
 * The bar under a table: what you are looking at, then the controls.
 *
 * The range used to sit in the filter bar at the top of the card, a full table
 * away from the Previous/Next buttons it describes — so paging changed a
 * number nowhere near where you clicked. `aria-live` announces the new range
 * for the same reason: nothing else about the page tells you the click landed.
 */
export function TablePagination({
  summary,
  previousLabel,
  nextLabel,
  canGoBack,
  canGoForward,
  onPrevious,
  onNext,
}: {
  summary: ReactNode;
  previousLabel: string;
  nextLabel: string;
  canGoBack: boolean;
  canGoForward: boolean;
  onPrevious: () => void;
  onNext: () => void;
}) {
  return (
    <div className="border-border/60 flex flex-wrap items-center justify-between gap-2 border-t p-3">
      <span className="text-muted-foreground px-1 text-sm tabular-nums" aria-live="polite">
        {summary}
      </span>
      <div className="flex items-center gap-2">
        <Button variant="outline" size="sm" disabled={!canGoBack} onClick={onPrevious}>
          <ChevronLeft aria-hidden />
          {previousLabel}
        </Button>
        <Button variant="outline" size="sm" disabled={!canGoForward} onClick={onNext}>
          {nextLabel}
          <ChevronRight aria-hidden />
        </Button>
      </div>
    </div>
  );
}

/** A single full-width row: the empty state, or a message about the data. */
export function TableMessageRow({ children }: { children: ReactNode }) {
  const columnCount = useContext(ColumnCountContext);
  return (
    <tr>
      <td colSpan={columnCount} className="text-muted-foreground px-4 py-12 text-center text-sm">
        {children}
      </td>
    </tr>
  );
}

/**
 * Placeholder rows for the first load.
 *
 * They hold the table's height so the pager and the card edge do not jump when
 * the rows land, which a centred "Loading…" line could not do. `aria-hidden`
 * keeps them out of the accessibility tree entirely — assistive tech should
 * hear the screen's live region say it is loading, not count twenty empty
 * rows — and, incidentally, out of `getAllByRole('row')` in tests.
 */
export function TableSkeletonRows({ rows = 8 }: { rows?: number }) {
  const columnCount = useContext(ColumnCountContext);
  return (
    <>
      {Array.from({ length: rows }, (_, rowIndex) => (
        <tr key={rowIndex} aria-hidden>
          {Array.from({ length: columnCount }, (_, columnIndex) => (
            <td key={columnIndex} className="border-border/60 border-b px-4 py-3">
              <Skeleton className={cn('h-4', columnIndex === 0 ? 'w-6' : 'w-20')} />
            </td>
          ))}
        </tr>
      ))}
    </>
  );
}
